import { useState, useEffect, useMemo, useId } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { isAxiosError } from "axios";
import { buttonVariants } from "@markeroo/theme";

import { apiAxios, API_ORIGIN } from "@/api/config";
import { MarkeerooBrand } from "@/components/MarkeerooBrand";
import { useNotification } from "@/providers/NotificationProvider";
import { useAnalytics } from "@/providers/AnalyticsProvider";
import { useAuthStore } from "@/stores/auth";
import { useShallow } from "zustand/shallow";
import { inputClass, mutedText } from "@/styles/variants";
import { ui, notifications } from "@/strings";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import { sanitizeRedirect } from "@/lib/security/redirects";

const logoUrl = new URL("../../images/logo-full.png", import.meta.url).href;

export function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { show } = useNotification();
  const analytics = useAnalytics();
  const { user, hydrated } = useAuthStore(useShallow((state) => ({
    user: state.user,
    hydrated: state.hydrated,
  })));

  // Pre-fill email from navigation state (when redirected from login)
  const prefillEmail = (location.state as { email?: string } | null)?.email || "";
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleRedirecting, setGoogleRedirecting] = useState(false);

  // Generate unique ID for error message accessibility
  const confirmPasswordErrorId = useId();

  useEffect(() => {
    if (hydrated && user) {
      void navigate("/", { replace: true });
    }
  }, [hydrated, user, navigate]);

  const nextPath = useMemo(() => {
    if (typeof window === "undefined") {
      return "/";
    }
    const params = new URLSearchParams(window.location.search ?? "");
    return sanitizeRedirect(params.get("next"));
  }, []);

  const passwordsMatch = password === confirmPassword;
  const isPasswordValid =
    password.length >= 12 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password) &&
    /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password);

  const canSubmit = email && password && confirmPassword && firstName && passwordsMatch && isPasswordValid;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordsMatch) {
      show(ui.labels.passwordsDoNotMatch, "error");
      return;
    }

    if (!isPasswordValid) {
      show(ui.labels.passwordRequirements, "error");
      return;
    }

    setIsSubmitting(true);

    apiAxios
      .post("/auth/register", {
        email,
        password,
        firstName,
        lastName: lastName || null,
        dpaAccepted: true,
      })
      .then(() => {
        analytics.track("register_email_success");
        // Store next path for after email verification
        if (nextPath && nextPath !== "/") {
          localStorage.setItem("postAuthRedirect", nextPath);
        }
        show(notifications.auth.registrationSuccess, "success");
        void navigate("/check-email", { state: { email } });
      })
      .catch((error: unknown) => {
        analytics.track("register_email_error");

        // Check for 409 CONFLICT (account exists) - redirect to login
        if (isAxiosError(error) && error.response?.status === 409) {
          show(notifications.auth.accountExists, "info");
          void navigate("/login", { state: { email } });
          return;
        }

        const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail;
        if (detail) {
          show(detail, "error");
        } else {
          show(notifications.auth.registrationFailed, "error");
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleGoogleSignIn = () => {
    setGoogleRedirecting(true);
    analytics.track("google_oauth_start");
    const target = `${API_ORIGIN}/api/auth/google?next=${encodeURIComponent(nextPath)}`;
    window.location.assign(target);
  };

  const googleButtonClass = `inline-flex ${buttonVariants.link} items-center justify-center gap-2`;

  const primaryButtonClass = `${buttonVariants.primary} w-full`;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 py-10">
      <div className="flex w-full max-w-md flex-col gap-6 rounded-2xl bg-white p-8 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <div className="flex flex-col items-center gap-2">
          <Link to="/">
            <img src={logoUrl} alt="Markeroo logo" className="h-24 w-auto" />
          </Link>
          <h1 className="text-2xl">
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <MarkeerooBrand />
            </Link>
          </h1>
          <h2 className="text-lg font-semibold text-text-theme-primary text-center">
            {ui.labels.createYourAccount}
          </h2>
        </div>

        {/* Google Sign In - Primary */}
        <button
          type="button"
          className={googleButtonClass}
          onClick={handleGoogleSignIn}
          disabled={isGoogleRedirecting || isSubmitting}
        >
          <GoogleIcon />
          <span>
            {isGoogleRedirecting ? ui.actions.redirecting : ui.actions.signInWithGoogle}
          </span>
        </button>

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-text-theme-muted">{ui.labels.orContinueWith}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="firstName" className="block text-sm font-medium text-text-theme-secondary">
                {ui.labels.firstName}
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={`${inputClass} mt-1`}
                required
                disabled={isSubmitting}
                autoComplete="given-name"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="lastName" className="block text-sm font-medium text-text-theme-secondary">
                {ui.labels.lastName}
                {" ("}
                {ui.labels.optional}
                {")"}
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={`${inputClass} mt-1`}
                disabled={isSubmitting}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-theme-secondary">
              {ui.labels.email}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputClass} mt-1`}
              required
              disabled={isSubmitting}
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text-theme-secondary">
              {ui.labels.password}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputClass} mt-1`}
              required
              disabled={isSubmitting}
              autoComplete="new-password"
            />
            <PasswordStrengthIndicator password={password} />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-theme-secondary">
              {ui.labels.confirmPassword}
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`${inputClass} mt-1 ${
                confirmPassword && !passwordsMatch ? "border-semantic-error" : ""
              }`}
              required
              disabled={isSubmitting}
              autoComplete="new-password"
              aria-describedby={confirmPassword && !passwordsMatch ? confirmPasswordErrorId : undefined}
              aria-invalid={confirmPassword && !passwordsMatch ? true : undefined}
            />
            {confirmPassword && !passwordsMatch && (
              <p id={confirmPasswordErrorId} className="mt-1 text-xs text-semantic-error" role="alert">
                {ui.labels.passwordsDoNotMatch}
              </p>
            )}
          </div>

          <button
            type="submit"
            className={primaryButtonClass}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? ui.actions.creatingAccount : ui.actions.createAccount}
          </button>
        </form>

        {/* Legal agreement notice */}
        <p className={`${mutedText} text-center text-xs`}>
          {ui.compliance.signUpAgreement}{" "}
          <Link to="/terms" className="text-brand-600 hover:text-brand-700" target="_blank" rel="noopener noreferrer">
            {ui.legal.termsOfService}
          </Link>
          {" and "}
          <Link to="/privacy" className="text-brand-600 hover:text-brand-700" target="_blank" rel="noopener noreferrer">
            {ui.legal.privacyPolicy}
          </Link>
        </p>

        {/* Footer link */}
        <p className={`${mutedText} text-center text-sm`}>
          {ui.labels.alreadyHaveAccount}{" "}
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
            {ui.actions.logIn}
          </Link>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <span aria-hidden="true" className="mr-2 text-text-theme-secondary">
      {/* eslint-disable no-restricted-syntax -- Google brand colors required for logo */}
      <svg className="h-5 w-5" viewBox="0 0 18 18" focusable="false">
        <path
          d="M17.64 9.2045c0-.638-.057-1.252-.163-1.841H9v3.481h4.844c-.209 1.125-.842 2.078-1.795 2.716v2.258h2.908c1.704-1.568 2.683-3.877 2.683-6.614z"
          fill="#4285F4"
        />
        <path
          d="M9 18c2.43 0 4.468-.806 5.957-2.183l-2.908-2.258c-.806.54-1.838.859-3.049.859-2.348 0-4.335-1.584-5.042-3.708H.957v2.327C2.438 15.978 5.481 18 9 18z"
          fill="#34A853"
        />
        <path
          d="M3.958 10.71a5.41 5.41 0 01-.282-1.71c0-.594.102-1.17.282-1.71V4.963H.957A9 9 0 000 9c0 1.467.352 2.853.957 4.037l3.001-2.327z"
          fill="#FBBC05"
        />
        <path
          d="M9 3.563c1.32 0 2.5.454 3.438 1.347l2.579-2.578C13.463.835 11.425 0 9 0 5.481 0 2.438 2.022.957 4.963l3.001 2.327C4.665 5.166 6.652 3.563 9 3.563z"
          fill="#EA4335"
        />
      </svg>
      {/* eslint-enable no-restricted-syntax */}
    </span>
  );
}
